import { Trans, useLingui } from "@lingui/react/macro";
import { cloneDeep } from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { SupportedLanguages, UserRole } from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { connectedRoles } from "../../../constants/constants";
import { supportedLanguages } from "../../../constants/enum-to-text";
import { hasAdminAccess } from "../../../constants/permissions";
import { createPatch } from "../../../utils/patch";
import { maybeAsArray } from "../../../utils/utils";
import { AdminConsole } from "../admin-console";
import { PatientsModal } from "../common/patients-modal";
import { AssignmentRows } from "./assignment-rows";
import { ScreeningCreditsRows, useScreeningCredits } from "./screening-credits";
import { UserEditDraft, UsersItem } from "./users";

export const UserEditDialog = observer(function UserEditDialog({
  item,
  admin,
  onClose,
  onSave,
}: {
  item: UsersItem;
  admin: AdminConsole;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useLingui();
  const [original] = useState(() => createInitialDraft(item));
  const [draft] = useState(() =>
    observable<UserEditDraft>(cloneDeep(original)),
  );
  const isScreeningUser = item.role === UserRole["screening-user"];
  const credits = useScreeningCredits(isScreeningUser ? item.uuid : null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <h3 className="mb-6 text-base font-bold">
        <Trans>Edit user: {item.username}</Trans>
      </h3>
      <Form
        className="flex flex-col min-h-0 flex-1"
        onSubmit={async () => {
          const patch = createPatch({ ...draft }, original);
          if (Object.keys(patch).length > 0) {
            await onSave(patch);
          }
          await credits.save();
          onClose();
        }}
      >
        <div className="overflow-y-auto min-h-0 px-1 py-1">
          <FormTable dataAdd>
            {hasAdminAccess(admin.api.permissions) && (
              <FormRow
                label={t`Email`}
                data={<TextInput attr="email" model={draft} />}
              />
            )}
            <FormRow
              label={t`Preferred language`}
              data={
                <Select
                  attr="preferred_language"
                  values={supportedLanguages}
                  model={draft}
                  required
                />
              }
              mandatory
            />

            {connectedRoles.includes(item.role) && (
              <FormRow
                label={t`Connected ID`}
                data={
                  <ChooseField
                    label={
                      draft.connected_patient_id != null
                        ? String(draft.connected_patient_id)
                        : t`None`
                    }
                    modal={({ onClose: closeModal }) => {
                      const patients = admin.createPatients();
                      return (
                        <PatientsModal
                          title={t`Change connected ID for user: ${item.username}`}
                          preselected={
                            maybeAsArray(draft.connected_patient_id) ??
                            undefined
                          }
                          mode="single"
                          model={patients}
                          onClose={closeModal}
                          onConfirm={({ selected }) => {
                            draft.connected_patient_id = selected[0] ?? null;
                          }}
                        />
                      );
                    }}
                  />
                }
              />
            )}

            {/* No `app`, so no inline creation: correcting an existing user's
                assignments is picking from what exists, and the wizard is
                where a missing physician or clinic gets made. */}
            <AssignmentRows
              draft={draft}
              admin={admin}
              username={item.username}
              userUuid={item.uuid}
            />

            {isScreeningUser && (
              <ScreeningCreditsRows
                credits={credits}
                username={item.username}
              />
            )}
          </FormTable>
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit">
            <Trans>Save</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});

function createInitialDraft(item: UsersItem): UserEditDraft {
  return {
    email: item.email ?? null,
    preferred_language: item.preferred_language ?? SupportedLanguages.sk,
    connected_patient_id: item.connected_patient_id ?? null,
    physicians: (item.assigned_physicians ?? []).map((p) => p.uuid),
    clinics: (item.assigned_clinics ?? []).map((c) => c.uuid),
  };
}
